import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Locale from 'data/locale';
import EditingContext from 'store/editingContext';
import Store from 'store';

import './ImportPane.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { replaceReact } from 'utils/format';
import { usePersistentState } from 'utils';
import useApi from 'api';
import importProfile, { classMap } from 'logic/import';
import History from 'store/history';
import { itemSetId } from 'logic/itemReducers';
import Dialog from 'ui/dialog';
import Select from 'ui/select';
import { ClassOption } from 'components/paperdoll/ClassSelect';
import { OverlayContext } from 'ui/utils/OverlayManager';

const _L = Locale("ui-import.js");

function formatNotice(text) {
  const replaceB = text => replaceReact(text, /<b>([\s\S]*?)<\/b>/gm, (m, text) => <b>{text}</b>);
  const replaceLi = text => replaceReact(text, /<li>([\s\S]*?)<\/li>/gm, (m, text) => <li>{replaceB(text)}</li>);
  return replaceReact(text, /<ul>([\s\S]*?)<\/ul>/gm, (m, text) => <ul>{replaceLi(text)}</ul>, replaceB);
}

function ProfileHeader({ data }) {
  const tag = data.battleTag.match(/^(.*)#(.*)$/);  
  if (!tag) return null;

  let url = `https://${data.region}.diablo3.com/en-us/profile/${data.battleTag.replace('#', '-')}/`;
  if (data.region === 'cn') {
    url = `https://d3.blizzard.cn/profile/${data.battleTag.replace('#', '-')}/`;
    if (data.heroes.length) {
      url += data.heroes[0].id;
    }
  }

  return (
    <div className="d3p-import-header">
      <a href={url} target="_blank" rel="noopener noreferrer">
        <span className="d3p-import-tag">{tag[1]}</span>
        <span className="d3p-import-hash">#{tag[2]}</span>
        {!!data.guildName && " "}
        {!!data.guildName && <span className="d3p-import-guild">&lt;{data.guildName}&gt;</span>}
      </a>
    </div>
  );
}

function ImportCharacter({ data, onImport }) {
  const charClass = classMap[data.class] || data.class;
  const gender = (data.gender === 0 ? "male" : "female");
  const date = new Date((data.death?.time || data["last-updated"]) * 1000);
  const onClick = React.useCallback(() => onImport(data.id), [onImport, data.id]);
  return (
    <li className={classNames({"d3p-hero-dead": data.dead})} onClick={onClick}>
      <span className={`d3p-class-icon d3p-class-${charClass} d3p-${gender}`}>&nbsp;</span>
      <span className="d3p-hero-name">{data.name}</span>
      <span className={classNames("d3p-hero-info", {"d3p-hero-hardcore": data.hardcore})}>
        {_L`Level ${data.level} ${Data.classes[charClass]?.name}`}
      </span>
      {!!data.seasonal && <span className="d3p-hero-seasonal" title={_L`Seasonal`}>&nbsp;</span>}
      {data.dead ? (
        <span className="d3p-hero-death">{_L`(Died ${date.toLocaleDateString()})`}</span>
      ) : (
        <span className="d3p-hero-updated">{_L`(updated ${date.toLocaleDateString()})`}</span>
      )}
    </li>
  );
}

function Autocomplete({ value, onChange, onEnter, placeholder, options, disabled }) {
  const [focused, setFocused] = React.useState(false);

  const setValue = React.useCallback(e => onChange(e.target.value), [onChange]);

  const inputRef = React.useRef();
  const onFocus = React.useCallback(() => {
    setFocused(true);
    inputRef.current.select();
  }, []);
  const onBlur = React.useCallback(() => setFocused(false), []);

  React.useEffect(() => {
    if (disabled) {
      setFocused(false);
    }
  }, [disabled]);

  const valueLower = value.toLowerCase();

  const filtered = React.useMemo(() => Object.entries(options)
    .filter(([, name]) => name.toLowerCase().includes(valueLower)).reverse().slice(0, 10).sort(),
    [options, valueLower]);

  const onKeyDown = React.useCallback(e => {
    if (e.key === "Enter") {
      onEnter(value);
    }
  }, [value, onEnter]);

  return (
    <div className="d3p-Autocomplete">
      <input type="text"
             ref={inputRef}
             value={value}
             onChange={setValue}
             placeholder={(!focused && placeholder) || null}
             onFocus={onFocus}
             onBlur={onBlur}
             onKeyDown={onKeyDown}
             disabled={disabled}
      />
      {focused && filtered.length > 0 && !disabled && (
        <ul className="d3p-options">
          {filtered.map(([index, name]) => (
            <li key={index} onMouseDown={() => { onChange(name); onEnter(name); }}>{name}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

const renderClass = cls => <ClassOption charClass={cls}/>;

function SelectFollower({ onResolve, onClose, followers }) {
  const options = Object.keys(followers);
  const [value, setValue] = React.useState(options[0]);
  return (
    <Dialog title={_L`Select Follower`} modal onClose={onClose}>
      <Dialog.Content>
        <p>{_L`Select follower:`}</p>
        <div><Select options={options} value={value} onChange={setValue} renderOption={renderClass} renderValue={renderClass}/></div>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve(value)}>{_L`Import`}</button>
        <button onClick={() => onResolve(null)}>{_L`Cancel`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

export default function ImportSection() {
  const [searching, setSearching] = EditingContext.useState("importSearching", false);
  const [error, setError] = EditingContext.useState("importError");
  const [result, setResult] = EditingContext.useState("importResult");
  const [, setSpinner] = EditingContext.useState("spinner");
  const api = useApi();

  const store = Store.useStore();
  const historyStore = History.useStore();
  const dispatch = Store.useDispatch();
  const editingDispatch = EditingContext.useDispatch();
  
  const [region, setRegion] = usePersistentState("importRegion", "eu");
  const onRegionChange = React.useCallback(e => setRegion(e.target.value), [setRegion]);
  
  const [tagOptions, setTagOptions] = usePersistentState("importTags", []);
  const [battleTag, setBattleTag] = React.useState("");

  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => mountedRef.current = false;
  }, []);

  const runSearch = React.useCallback((region, tag) => {
    setSearching(true);
    setTagOptions(tags => tags.includes(tag) ? tags : [...tags, tag]);
    api.importProfile(region, tag).then(
      result => {
        if (result.heroes) result.heroes.sort((a, b) => b["last-updated"] - a["last-updated"]);
        setResult(result);
        setError(null);
      },
      error => {
        setResult(null);
        setError(error.message);
      },
    ).finally(() => setSearching(false));
  }, [api, setTagOptions, setSearching, setResult, setError]);

  const overlays = React.useContext(OverlayContext);
  const onImport = React.useCallback(id => {
    if (result.region && result.battleTag) {
      setSpinner(spin => spin + 1);
      api.importCharacter(result.region, result.battleTag, id).then(
        async result => {
          const state = store.getState();
          const profile = state.profiles[state.activeProfile];
          const canUndo = historyStore.getState()[profile.id]?.prev;

          let imported = importProfile(result);
          if (imported?.followers && Object.values(imported.followers).filter(Boolean).length > 1) {
            const follower = await overlays.async(<SelectFollower followers={imported.followers}/>);
            if (!follower) return;
            const items = imported.followers[follower].items;
            imported = {
              ...imported.profile,
              follower,
              followerItems: {},
              followerSkills: imported.followers[follower].skills,
            };
            for (const slot in items) {
              if (typeof items[slot] === 'string') {
                imported.followerItems[slot] = itemSetId(null, items[slot], follower);
              } else {
                imported.followerItems[slot] = items[slot];
              }
            }
          } else {
            imported = imported.profile;
          }
          if (imported) {
            if (canUndo) {
              dispatch.addProfile(imported);
            } else {
              dispatch.setProfile(imported);
            }
            editingDispatch({type: "paragon"});
            setError(null);
          } else {
            setError(_L`Invalid character.`);
          }
        },
        error => setError(error.message),
      ).finally(() => setSpinner(spin => spin - 1));
    }
  }, [result, api, dispatch, editingDispatch, setError, store, historyStore, setSpinner, overlays]);

  const onSearch = React.useCallback(() => runSearch(region, battleTag), [runSearch, region, battleTag]);
  const onTagEnter = React.useCallback(tag => runSearch(region, tag), [runSearch, region]);

  return (
    <div className="d3p-SectionContent d3p-section-import">
      <div className="d3p-description">
        {formatNotice(_L("BNImportNotice"))}
        {false && <div style={{color: '#f00', fontSize: '1.2em'}}>Battle.net API!</div>}
      </div>
      <div className="d3p-import-options">
        <select value={region} onChange={onRegionChange} disabled={searching}>
          <option value="eu">EU</option>
          <option value="us">US</option>
          <option value="tw">TW</option>
          <option value="kr">KR</option>
          <option value="cn">CN</option>
        </select>

        <Autocomplete value={battleTag} onChange={setBattleTag} placeholder="Tag#1234" options={tagOptions} onEnter={onTagEnter} disabled={searching}/>

        <button onClick={onSearch} disabled={!battleTag.match(/^.*[-#]\d+$/) || searching}>
          {searching && <FontAwesomeIcon icon={faSpinner} spin/>}
          {searching ? _L`Searching` : _L`Search`}
        </button>
      </div>
      {!!(result && result.heroes) && <ProfileHeader data={result}/>}
      {!!(result && result.heroes) && (
        <ul className="d3p-search-results">
          {result.heroes.map(hero => <ImportCharacter key={hero.id} data={hero} onImport={onImport}/>)}
        </ul>
      )}
      {!!error && <div className="d3p-import-error">{error}</div>}
    </div>
  );
}
